import { useEffect, useState } from "react";
import type { PauseReason } from "../api/useTableQuery";

export interface LiveBinding {
  live: boolean;
  onLiveChange: (next: boolean) => void;
  /** Non-null when the toggle is on but polling is suspended anyway. */
  pauseReason: PauseReason;
  /** Epoch ms of the last successful fetch (react-query's dataUpdatedAt). */
  dataUpdatedAt: number;
  isFetching: boolean;
}

function ago(sinceMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.round((nowMs - sinceMs) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
}

/** Re-render once a second so the relative timestamp actually counts up.
 *  Refetches alone are not enough: between two 5s polls the label would sit
 *  frozen at "just now", and while paused it would never move at all -- which
 *  is exactly when the user most needs to know how stale the table is. */
function useSecondTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function LiveToggle({ binding, offset, limit }: {
  binding: LiveBinding;
  offset: number;
  limit: number;
}) {
  const now = useSecondTick();
  const { live, onLiveChange, pauseReason, dataUpdatedAt, isFetching } = binding;

  const pauseNote =
    pauseReason === "page" ? `Paused — page ${Math.floor(offset / limit) + 1}`
    : pauseReason === "hidden" ? "Paused — tab hidden"
    : null;

  return (
    <div className="live-bar">
      <button
        type="button"
        role="switch"
        aria-checked={live}
        aria-label="Live updates"
        className={`live-switch${live ? " on" : ""}`}
        onClick={() => onLiveChange(!live)}
      >
        <span className={`live-dot${live && !pauseReason ? " pulsing" : ""}`} aria-hidden="true" />
        Live
      </button>

      {pauseNote && <span className="live-note">{pauseNote}</span>}

      {/* aria-live=off deliberately: a timestamp that changes every second
          would otherwise be announced every second by a screen reader. */}
      <span className="live-updated" aria-live="off">
        {isFetching ? "updating…" : `updated ${ago(dataUpdatedAt, now)}`}
      </span>
    </div>
  );
}
